import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import Register from './components/Register';
import CharacterSelection from './components/CharacterSelection';
import AbilityTypeSelection from './components/AbilityTypeSelection';
import IntroVideo from './components/IntroVideo';
import Game from './components/Game';
import Inventory from './components/Inventory';
import Achievements from './components/Achievements';
import Arena from './components/Arena';
import Village from './components/Village';
import Battle from './components/Battle';
import Fishing from './components/Fishing';
import Mining from './components/Mining';
import Farming from './components/Farming';
import Work from './components/Work';
import Craft from './components/Craft';
import Market from './components/Market';
import Wiki from './components/Wiki';
import Profile from './components/Profile';
import BottomNav from './components/BottomNav';
import Dungeon from './components/Dungeon';
import FriendNotifier from './components/FriendNotifier';
import GlobalChat from './components/GlobalChat';
import ItemMarketButton from './components/ItemMarketButton';
import ItemMarket from './components/ItemMarket';
import QuestButton from './components/QuestButton';
import LevelQuests from './components/LevelQuests';
import { QuestProvider } from './context/QuestContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import GameSettings from './components/GameSettings';
import './App.css';
import './components/ScreenFit.css';
import { getStoredLanguage } from './i18n';
import { ToastProvider } from './context/ToastContext';
import { ItemMarketProvider } from './context/ItemMarketContext';
import { GameConfigProvider } from './context/GameConfigContext';
import { CharacterProvider, useCharacterOptional } from './context/CharacterContext';
import { AudioSettingsProvider } from './context/AudioSettingsContext';
import DevModeBanner from './components/DevModeBanner';
import TestPhaseBanner from './components/TestPhaseBanner';
import AssetLoadingScreen from './components/AssetLoadingScreen';
import { playButtonClickSound, preloadButtonSounds, shouldPlayButtonClickSound } from './utils/buttonClickSound';
import { canPlayMusic, subscribeAudioSettings } from './utils/audioSettings';
import { isInvalidTokenError, redirectToLoginForInvalidSession } from './utils/authSession';
import { registerNativePushFromSession } from './utils/mobilePushBridge';
import { startImagePreload, subscribeImagePreloadProgress } from './utils/imageCache';
import DepotNotice from './components/DepotNotice';

// Oyunla ilgili tüm sayfa path'leri (köy müziği bu sayfalarda çalmalı)
const GAME_PATHS = ['/game', '/village', '/battle', '/travel', '/inventory', '/development', '/achievements', '/arena', '/work', '/fishing', '/mining', '/farming', '/market', '/wiki', '/dungeon'];

// Giriş / kayıt / karakter seçim akışı (açılış müziği)
const AUTH_PATHS = ['/login', '/register', '/character-selection', '/ability-type-selection'];

// Görsel preload bitene kadar bekletilecek sayfalar
const ASSET_GATED_PATHS = [
  ...GAME_PATHS,
  '/character-selection',
  '/ability-type-selection',
  '/intro',
];

const BATTLE_MUSIC_TRACKS = ['savas1.mp3', 'savas2.mp3', 'savas3.mp3'];
const BATTLE_MUSIC_MODE_PREFIX = 'battle-playlist:';
const FISHING_MUSIC_TRACKS = ['balik1.mp3', 'balik2.mp3'];
const FISHING_MUSIC_MODE_PREFIX = 'fishing-playlist:';
const MINING_MUSIC_TRACKS = ['maden1.mp3', 'maden2.mp3'];
const MINING_MUSIC_MODE_PREFIX = 'mining-playlist:';

function resolveBattleTracks(villageBattleMusic) {
  if (Array.isArray(villageBattleMusic) && villageBattleMusic.length > 0) {
    return villageBattleMusic;
  }
  return BATTLE_MUSIC_TRACKS;
}

function battlePlaylistKey(tracks) {
  return `${BATTLE_MUSIC_MODE_PREFIX}${tracks.join('|')}`;
}

function fishingPlaylistKey(tracks) {
  return `${FISHING_MUSIC_MODE_PREFIX}${tracks.join('|')}`;
}

function miningPlaylistKey(tracks) {
  return `${MINING_MUSIC_MODE_PREFIX}${tracks.join('|')}`;
}
const HOME_MUSIC_VOLUME = 0.3;
const GAME_MUSIC_VOLUME = 0.3; // köy, menü, envanter vb. (anasayfa ve savaş hariç)
const BATTLE_MUSIC_VOLUME = 0.35;

function shuffleTracks(tracks) {
  const list = [...tracks];
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

// Buton tıklama sesi: savaş dışı tüm butonlar; savaşta yalnızca üst nav (.battle-nav-row)
const ButtonClickSound = () => {
  const location = useLocation();

  useEffect(() => {
    // Sesler ilk dokunuşta yüklenir (mobil tarayıcılar sesi kullanıcı etkileşimi sonrası açar).
    const warm = () => preloadButtonSounds();
    document.addEventListener('pointerdown', warm, { once: true, capture: true });
    return () => document.removeEventListener('pointerdown', warm, { capture: true });
  }, []);

  useEffect(() => {
    const onClick = (event) => {
      const button = event.target.closest('button');
      if (!shouldPlayButtonClickSound(button, location.pathname)) return;
      playButtonClickSound();
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [location.pathname]);

  return null;
};

// Müzik yönetimi için ayrı component
// Oyun müziğinin devam ettiği ek sayfalar (GAME_PATHS dışındakiler).
const MUSIC_EXTRA_PATHS = ['/profile', '/craft'];

const MusicManager = () => {
  const location = useLocation();
  const audioRef = useRef(null);
  const currentMusicRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const isBattleMusicRef = useRef(false);
  const isFishingMusicRef = useRef(false);
  const isMiningMusicRef = useRef(false);
  const battleQueueRef = useRef([]);
  const battleIndexRef = useRef(0);
  const battleTracksRef = useRef(BATTLE_MUSIC_TRACKS);
  const fishingQueueRef = useRef([]);
  const fishingIndexRef = useRef(0);
  const fishingTracksRef = useRef(FISHING_MUSIC_TRACKS);
  const miningQueueRef = useRef([]);
  const miningIndexRef = useRef(0);
  const miningTracksRef = useRef(MINING_MUSIC_TRACKS);
  const musicEnabledRef = useRef(true);
  const characterCtx = useCharacterOptional();
  const villageMusic = characterCtx?.character?.villageMusic;
  const villageBattleMusic = characterCtx?.character?.villageBattleMusic;
  const hasCharacter = characterCtx?.hasCharacter;
  // Profil vb. oyun içi menülerde köy müziği kesilmesin.
  const isGamePath = GAME_PATHS.includes(location.pathname) || MUSIC_EXTRA_PATHS.includes(location.pathname);
  const isBattlePath = location.pathname === '/battle';
  const isFishingPath = location.pathname === '/fishing';
  const isMiningPath = location.pathname === '/mining';

  useEffect(() => {
    musicEnabledRef.current = canPlayMusic();
    return subscribeAudioSettings((settings) => {
      musicEnabledRef.current = canPlayMusic(settings);
      const audio = audioRef.current;
      if (!audio) return;
      if (!musicEnabledRef.current) {
        audio.pause();
      } else if (audio.src && audioUnlockedRef.current) {
        audio.play().catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    const onPauseBackgroundMusic = () => {
      const audio = audioRef.current;
      if (!audio) return;
      audio.pause();
      audio.currentTime = 0;
      currentMusicRef.current = null;
    };
    // Hikâye videosu kapanınca müzik kaldığı yerden değil baştan devam eder.
    const onResumeBackgroundMusic = () => {
      const audio = audioRef.current;
      if (!audio || !audio.src || !musicEnabledRef.current || !audioUnlockedRef.current) return;
      audio.play().catch(() => {});
    };
    window.addEventListener('text2:pause-background-music', onPauseBackgroundMusic);
    window.addEventListener('text2:resume-background-music', onResumeBackgroundMusic);
    return () => {
      window.removeEventListener('text2:pause-background-music', onPauseBackgroundMusic);
      window.removeEventListener('text2:resume-background-music', onResumeBackgroundMusic);
    };
  }, []);

  // Uygulama arka plana atılınca (sekme gizli / telefon kilitli) müzik durur, dönünce devam eder.
  useEffect(() => {
    const onVisibility = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (document.hidden) {
        audio.pause();
      } else if (audio.getAttribute("src") && musicEnabledRef.current) {
        audio.play().catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  const tryPlayMusic = () => {
    if (!musicEnabledRef.current || document.hidden) return;
    audioRef.current?.play().catch(() => {});
  };

  const playNextBattleTrack = () => {
    const audio = audioRef.current;
    if (!audio || !isBattleMusicRef.current) return;

    const tracks = battleTracksRef.current;
    if (!tracks.length) return;

    if (battleIndexRef.current >= battleQueueRef.current.length) {
      battleQueueRef.current = shuffleTracks(tracks);
      battleIndexRef.current = 0;
    }

    const file = battleQueueRef.current[battleIndexRef.current];
    battleIndexRef.current += 1;
    audio.loop = false;
    audio.src = `/music/${encodeURIComponent(file)}`;
    audio.volume = BATTLE_MUSIC_VOLUME;
    tryPlayMusic();
  };

  const playNextFishingTrack = () => {
    const audio = audioRef.current;
    if (!audio || !isFishingMusicRef.current) return;

    const tracks = fishingTracksRef.current;
    if (!tracks.length) return;

    if (fishingIndexRef.current >= fishingQueueRef.current.length) {
      fishingQueueRef.current = shuffleTracks(tracks);
      fishingIndexRef.current = 0;
    }

    const file = fishingQueueRef.current[fishingIndexRef.current];
    fishingIndexRef.current += 1;
    audio.loop = false;
    audio.src = `/music/${encodeURIComponent(file)}`;
    audio.volume = GAME_MUSIC_VOLUME;
    tryPlayMusic();
  };

  const playNextMiningTrack = () => {
    const audio = audioRef.current;
    if (!audio || !isMiningMusicRef.current) return;

    const tracks = miningTracksRef.current;
    if (!tracks.length) return;

    if (miningIndexRef.current >= miningQueueRef.current.length) {
      miningQueueRef.current = shuffleTracks(tracks);
      miningIndexRef.current = 0;
    }

    const file = miningQueueRef.current[miningIndexRef.current];
    miningIndexRef.current += 1;
    audio.loop = false;
    audio.src = `/music/${encodeURIComponent(file)}`;
    audio.volume = GAME_MUSIC_VOLUME;
    tryPlayMusic();
  };

  // Tarayıcı autoplay politikası: ilk kullanıcı tıklamasında ses kilidini aç
  useEffect(() => {
    const unlock = () => {
      if (audioUnlockedRef.current) return;
      audioUnlockedRef.current = true;
      if (audioRef.current && audioRef.current.src) {
        tryPlayMusic();
      }
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Savaş müziği: parça bitince sıradaki (rastgele karışık) parçaya geç
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const onEnded = () => {
      if (isBattleMusicRef.current) {
        playNextBattleTrack();
      } else if (isFishingMusicRef.current) {
        playNextFishingTrack();
      } else if (isMiningMusicRef.current) {
        playNextMiningTrack();
      }
    };

    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, []);

  useEffect(() => {
    const audio = audioRef.current;

    // Savaş ekranı: köye özel müzik varsa onu, yoksa savas1–3 rastgele sırayla çal
    if (isBattlePath) {
      const tracks = resolveBattleTracks(villageBattleMusic);
      const playlistKey = battlePlaylistKey(tracks);
      isBattleMusicRef.current = true;
      isFishingMusicRef.current = false;
      isMiningMusicRef.current = false;
      battleTracksRef.current = tracks;
      if (currentMusicRef.current !== playlistKey) {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        currentMusicRef.current = playlistKey;
        battleQueueRef.current = shuffleTracks(tracks);
        battleIndexRef.current = 0;
        playNextBattleTrack();
      } else if (audio?.paused) {
        audio.volume = BATTLE_MUSIC_VOLUME;
        tryPlayMusic();
      } else if (audio) {
        audio.volume = BATTLE_MUSIC_VOLUME;
      }
      return;
    }

    // Balık tutma ekranı: balik1 / balik2 döngü
    if (isFishingPath) {
      const tracks = FISHING_MUSIC_TRACKS;
      const playlistKey = fishingPlaylistKey(tracks);
      isBattleMusicRef.current = false;
      isFishingMusicRef.current = true;
      isMiningMusicRef.current = false;
      fishingTracksRef.current = tracks;
      if (currentMusicRef.current !== playlistKey) {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        currentMusicRef.current = playlistKey;
        fishingQueueRef.current = shuffleTracks(tracks);
        fishingIndexRef.current = 0;
        playNextFishingTrack();
      } else if (audio?.paused) {
        audio.volume = GAME_MUSIC_VOLUME;
        tryPlayMusic();
      } else if (audio) {
        audio.volume = GAME_MUSIC_VOLUME;
      }
      return;
    }

    // Madencilik ekranı: maden1 / maden2 döngü
    if (isMiningPath) {
      const tracks = MINING_MUSIC_TRACKS;
      const playlistKey = miningPlaylistKey(tracks);
      isBattleMusicRef.current = false;
      isFishingMusicRef.current = false;
      isMiningMusicRef.current = true;
      miningTracksRef.current = tracks;
      if (currentMusicRef.current !== playlistKey) {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
        currentMusicRef.current = playlistKey;
        miningQueueRef.current = shuffleTracks(tracks);
        miningIndexRef.current = 0;
        playNextMiningTrack();
      } else if (audio?.paused) {
        audio.volume = GAME_MUSIC_VOLUME;
        tryPlayMusic();
      } else if (audio) {
        audio.volume = GAME_MUSIC_VOLUME;
      }
      return;
    }

    isBattleMusicRef.current = false;
    isFishingMusicRef.current = false;
    isMiningMusicRef.current = false;

    // Intro / karaktersiz onboarding: arka plan müziği kapalı
    if (location.pathname === '/intro' || (location.pathname === '/game' && !hasCharacter)) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      currentMusicRef.current = null;
      return;
    }

    // Login veya Register sayfasındaysa home.mp3 çal (açılış müziği)
    if (AUTH_PATHS.includes(location.pathname)) {
      if (currentMusicRef.current !== 'home.mp3') {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        currentMusicRef.current = 'home.mp3';
        if (audioRef.current) {
          audioRef.current.src = '/music/home.mp3';
          audioRef.current.loop = true;
          audioRef.current.volume = HOME_MUSIC_VOLUME;
          tryPlayMusic();
        }
      } else if (audioRef.current && audioRef.current.paused) {
        audioRef.current.volume = HOME_MUSIC_VOLUME;
        tryPlayMusic();
      } else if (audioRef.current) {
        audioRef.current.volume = HOME_MUSIC_VOLUME;
      }
    }
    // Oyun sayfalarındaysa (battle hariç) köy müziği çal
    else if (isGamePath && villageMusic) {
      const musicFile = villageMusic;
      if (currentMusicRef.current !== musicFile) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
        }
        currentMusicRef.current = musicFile;
        if (audioRef.current) {
          // Özel karakterleri (ö, ü vb.) URL-safe encode et
          const encodedFile = encodeURIComponent(musicFile);
          audioRef.current.src = `/music/${encodedFile}`;
          audioRef.current.loop = true;
          audioRef.current.volume = GAME_MUSIC_VOLUME;
          tryPlayMusic();
        }
      } else if (audioRef.current && audioRef.current.paused) {
        audioRef.current.volume = GAME_MUSIC_VOLUME;
        tryPlayMusic();
      } else if (audioRef.current) {
        audioRef.current.volume = GAME_MUSIC_VOLUME;
      }
    }
    // Oyun sayfalarından çıkıldığında müziği durdur
    else if (!isGamePath && currentMusicRef.current && currentMusicRef.current !== 'home.mp3') {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
      currentMusicRef.current = null;
    }
  }, [location.pathname, villageMusic, villageBattleMusic, isGamePath, isBattlePath, isFishingPath, isMiningPath, hasCharacter]);

  return <audio ref={audioRef} preload="auto" />;
};

/** Açılış: kayıtlı oturum varsa oyuna, yoksa login'e. */
const HomeRedirect = () => {
  const hasSession = Boolean(
    localStorage.getItem('username') && localStorage.getItem('token'),
  );
  return <Navigate to={hasSession ? '/game' : '/login'} replace />;
};

// App içeriği
const AppRoutes = ({ setIsAuthenticated }) => (
  <Routes>
    <Route path="/login" element={<Login setIsAuthenticated={setIsAuthenticated} />} />
    <Route path="/register" element={<Register />} />
    <Route path="/intro" element={<IntroVideo />} />
    <Route path="/character-selection" element={<CharacterSelection />} />
    <Route path="/ability-type-selection" element={<AbilityTypeSelection />} />
    <Route path="/game" element={<Game />} />
    <Route path="/travel" element={<Navigate to="/village?tab=travel" replace />} />
    <Route path="/inventory" element={<Inventory />} />
    <Route path="/achievements" element={<Achievements />} />
    <Route path="/arena" element={<Arena />} />
    <Route path="/dungeon" element={<Dungeon />} />
    <Route path="/village" element={<Village />} />
    <Route path="/battle" element={<Battle />} />
    <Route path="/development" element={<Navigate to="/inventory?tab=development" replace />} />
    <Route path="/work" element={<Work />} />
    <Route path="/fishing" element={<Fishing />} />
    <Route path="/mining" element={<Mining />} />
    <Route path="/farming" element={<Farming />} />
    <Route path="/craft" element={<Craft />} />
    <Route path="/market" element={<Market />} />
    <Route path="/wiki" element={<Wiki />} />
    <Route path="/profile" element={<Profile />} />
    <Route path="/" element={<HomeRedirect />} />
  </Routes>
);

const AppContent = () => {
  const [, setIsAuthenticated] = useState(false);
  const location = useLocation();
  const username = localStorage.getItem('username');
  const isGamePath = GAME_PATHS.includes(location.pathname);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use((config) => {
      const lang = getStoredLanguage();
      config.headers = config.headers || {};
      config.headers['Accept-Language'] = lang;
      return config;
    });

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (
          error?.response?.status === 401 ||
          error?.response?.status === 403 ||
          isInvalidTokenError(error)
        ) {
          redirectToLoginForInvalidSession();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const shell = (
    <>
      <DevModeBanner />
      <TestPhaseBanner />
      <MusicManager />
      <ButtonClickSound />
      <AppRoutes setIsAuthenticated={setIsAuthenticated} />
      {username && <BottomNav />}
      {username && !AUTH_PATHS.includes(location.pathname) && location.pathname !== '/intro' && (
        <FriendNotifier username={username} />
      )}
      {isGamePath && username && (
        <>
          <ItemMarketButton />
          <QuestButton username={username} />
          <ItemMarket />
          <LevelQuests username={username} />
          <GlobalChat username={username} />
          <DepotNotice />
        </>
      )}
    </>
  );

  if (username) {
    return (
      <CharacterProvider username={username}>
        {shell}
      </CharacterProvider>
    );
  }

  return shell;
};

const AssetPreloadGate = ({ children }) => {
  const location = useLocation();
  const [progress, setProgress] = useState({ loaded: 0, total: 0 });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const unsubscribe = subscribeImagePreloadProgress(setProgress);
    startImagePreload()
      .then(() => setReady(true))
      .catch(() => setReady(true));
    return unsubscribe;
  }, []);

  const needsGate = ASSET_GATED_PATHS.includes(location.pathname);
  if (needsGate && !ready) {
    return (
      <AssetLoadingScreen loaded={progress.loaded} total={progress.total} />
    );
  }

  return children;
};

function AppShell() {
  const location = useLocation();
  const isCompact = location.pathname !== '/battle';

  return (
    <div className="app-shell">
      <GameSettings />
      <LanguageSwitcher />
      <aside className="app-side app-side--left" aria-hidden="true">
        <img
          src="/image/menu/solsagmenu.png"
          alt=""
          className="app-side__img"
          draggable={false}
        />
      </aside>
      <div className="app-center">
        <main className={`app-main${isCompact ? ' app-ui-compact' : ''}`}>
          <AssetPreloadGate>
            <AppContent />
          </AssetPreloadGate>
        </main>
        {/* Alt çubuk parçalardan: uç kapakları ve orta çıkıntı sabit, aradaki düz kısım uzar. */}
        <footer className="app-footer-bar" aria-hidden="true">
          <span className="app-footer-bar__part app-footer-bar__cap app-footer-bar__cap--left" style={{ backgroundImage: 'url("/image/menu/altmenu-sol.png")' }} />
          <span className="app-footer-bar__part app-footer-bar__fill app-footer-bar__fill--left" style={{ backgroundImage: 'url("/image/menu/altmenu-dolgu-sol.png")' }} />
          <span className="app-footer-bar__part app-footer-bar__mid" style={{ backgroundImage: 'url("/image/menu/altmenu-orta.png")' }} />
          <span className="app-footer-bar__part app-footer-bar__fill app-footer-bar__fill--right" style={{ backgroundImage: 'url("/image/menu/altmenu-dolgu-sag.png")' }} />
          <span className="app-footer-bar__part app-footer-bar__cap app-footer-bar__cap--right" style={{ backgroundImage: 'url("/image/menu/altmenu-sag.png")' }} />
        </footer>
      </div>
      <aside className="app-side app-side--right" aria-hidden="true">
        <img
          src="/image/menu/solsagmenu.png"
          alt=""
          className="app-side__img app-side__img--mirror"
          draggable={false}
        />
      </aside>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ToastProvider>
        <ItemMarketProvider>
          <QuestProvider>
          <GameConfigProvider>
            <AudioSettingsApp>
              <div className="App">
                <AppShell />
              </div>
            </AudioSettingsApp>
          </GameConfigProvider>
          </QuestProvider>
        </ItemMarketProvider>
      </ToastProvider>
    </Router>
  );
}

function AudioSettingsApp({ children }) {
  useLocation(); // login sonrası username yeniden okunsun
  const username = localStorage.getItem('username');

  useEffect(() => {
    if (username) {
      registerNativePushFromSession();
    }
  }, [username]);

  return (
    <AudioSettingsProvider username={username}>
      {children}
    </AudioSettingsProvider>
  );
}

export default App;
